import { OrderCorrectionTarget } from '../../services/OrderCorrections/OrderCorrectionModels';
import { actionIsAllowedForOrderLifecycle } from '../../components/Orders/orderCorrectionUi';
import { OrderPendingInvoicingHistoryItem } from '../../services/OrderPendingInvoicingHistories/GetOrderPendingInvoicingHistoryList/GetOrderPendingInvoicingHistoryListOutput';

// textos do "O que fazer" montados a partir do diagnóstico gravado pelo job (sem consultar o Quatro Olhos)

export interface IResolutionAction {
  code: string;
  title: string;
  description: string;
  fields: string[];
}

export interface IResolutionSteps {
  actions: IResolutionAction[];
  notes: string[];
}

interface IStoredAction {
  code?: string;
  target?: string;
  editableFields?: string[];
}

interface IStoredAttempts {
  totalAttempts?: number;
  maxAttempts?: number | null;
  lastAttemptAt?: string | null;
  nextAttemptAt?: string | null;
  automaticRetryAvailable?: boolean;
}

const fieldLabels: Record<string, string> = {
  cpf_cnpj: 'CPF/CNPJ',
  address: 'Endereço',
  street: 'Logradouro',
  number: 'Número',
  neighborhood: 'Bairro',
  complement: 'Complemento',
  city: 'Cidade',
  state: 'UF',
  zip_code: 'CEP',
  country_code: 'País',
  payment: 'Pagamento',
  ncc: 'NCC',
  classification: 'Classificação',
  configuration: 'Configuração'
};

const actionTexts: Record<string, { title: string; description: string }> = {
  [OrderCorrectionTarget.Shopify]: {
    title: 'Corrigir os dados do cliente na Shopify',
    description: 'Ajustar no pedido da Shopify os campos indicados e aguardar o Quatro Olhos reenviar ao Protheus.'
  },
  [OrderCorrectionTarget.ProtheusCustomer]: {
    title: 'Corrigir o cadastro do cliente no Protheus',
    description: 'Ajustar no cadastro do cliente no Protheus os campos indicados.'
  },
  [OrderCorrectionTarget.Reprocess]: {
    title: 'Reprocessar o pedido',
    description: 'Depois de resolver a causa, reenviar o pedido do Quatro Olhos para o Protheus.'
  },
  [OrderCorrectionTarget.InvoiceDetailsRefresh]: {
    title: 'Obter os detalhes da nota fiscal',
    description: 'Forçar o Quatro Olhos a buscar novamente no Protheus os detalhes da nota já emitida.'
  },
  [OrderCorrectionTarget.InvoiceDetailsRefreshAndTransmit]: {
    title: 'Obter os detalhes da nota e transmitir',
    description: 'Buscar os detalhes da nota no Protheus e concluir a transmissão do faturamento.'
  }
};

const informationalTexts: Record<string, string> = {
  wait_for_retry: 'Aguardar a próxima tentativa automática do Quatro Olhos antes de mexer nos dados.',
  review_configuration: 'A configuração da integração precisa ser revisada por uma pessoa responsável.',
  review_manually: 'O caso exige revisão manual; não há correção automática para ele.'
};

const lifecycleLabels: Record<string, string> = {
  not_created: 'Não registrado no Protheus',
  created: 'Registrado no Protheus',
  invoiced: 'Faturado'
};

const integrationStatusLabels: Record<string, string> = {
  pending: 'Na fila',
  processing: 'Processando',
  partial: 'Parcial',
  done: 'Concluída',
  failed: 'Falhou',
  skipped: 'Ignorada'
};

export const fieldLabel = (field: string) => fieldLabels[field] || field;

export const lifecycleLabel = (lifecycle?: string) => (lifecycle && lifecycleLabels[lifecycle]) || lifecycle || '';

export const integrationStatusLabel = (status?: string) =>
  (status && integrationStatusLabels[status.toLowerCase()]) || status || '';

export const severityVariant = (severity?: string) => {
  if (severity === 'error') return 'danger';
  if (severity === 'info') return 'info';
  return 'warning';
};

export const getResolutionSteps = (item: OrderPendingInvoicingHistoryItem): IResolutionSteps => {
  const storedActions: IStoredAction[] = Array.isArray(item.availableActions) ? item.availableActions : [];
  const actions: IResolutionAction[] = [];
  const notes: string[] = [];
  let blockedByLifecycle = false;

  storedActions.forEach(action => {
    const code = action.code || '';
    const target = action.target || OrderCorrectionTarget.None;
    const texts = actionTexts[target];

    if (!texts) {
      const note = informationalTexts[code] || informationalTexts.review_manually;
      if (notes.indexOf(note) < 0) notes.push(note);
      return;
    }

    if (!actionIsAllowedForOrderLifecycle(target, item.createdOnProtheus, item.invoiced)) {
      blockedByLifecycle = true;
      return;
    }

    actions.push({
      code,
      title: texts.title,
      description: texts.description,
      fields: action.editableFields || []
    });
  });

  if (blockedByLifecycle) {
    notes.push('O pedido já foi registrado no Protheus ou faturado: correções de cadastro e reprocessamento não se aplicam.');
  }

  return { actions, notes };
};

export const getAttempts = (item: OrderPendingInvoicingHistoryItem): IStoredAttempts | undefined =>
  item.attempts && typeof item.attempts === 'object' ? item.attempts as IStoredAttempts : undefined;

export const formatAttempts = (attempts?: IStoredAttempts) => {
  if (!attempts || attempts.totalAttempts === undefined) return '';
  return attempts.maxAttempts ? `${attempts.totalAttempts} de ${attempts.maxAttempts}` : `${attempts.totalAttempts}`;
};
